import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CHAT_MESSAGE_MAX_LENGTH,
  ChatGroupDetailSchema,
  ChatGroupSchema,
  ChatMessageSchema,
  type ChatGroupDetailDto,
  type ChatMessageDto,
  type ChatPersonDto,
  type MeResponse,
} from '@taqon/contracts';
import { z } from 'zod';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { errorMessage, getData, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog } from '../../shared/ui/forms';
import { PersonName } from '../../shared/ui/person';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { relativeTime } from '../notifications/inbox';
import { CHAT_GROUPS, CHAT_GROUPS_KEY, MESSAGE_PAGE, placeholderFor } from './chat-api';
import { PersonPicker } from './PersonPicker';

const GroupsSchema = z.array(ChatGroupSchema);
const MessagesSchema = z.array(ChatMessageSchema);
const groupKey = (id: string) => [...CHAT_GROUPS_KEY, id] as const;

/**
 * Group chats (Administration; FRD-CORE-048, 049, 052, 057; SDD §29.11): create a group and choose
 * its members. Privileged (`platform.chat_group`). One-to-one chats are not listed here and cannot
 * be reached from here (FRD-CORE-047).
 */
export function ChatGroupsPage() {
  const session = useSession();
  const me = session.data;
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const groups = useQuery({
    queryKey: CHAT_GROUPS_KEY,
    queryFn: () => getData(CHAT_GROUPS, GroupsSchema),
  });
  if (me === null || me === undefined) return null;
  const canCreate = hasCapability(me, 'platform.chat_group:create');

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Group chats"
        description="Group chats and their members. Only members can see or post in a group; archived groups are read-only."
        actions={
          canCreate ? (
            <Button
              variant="contained"
              onClick={() => {
                setCreating(true);
              }}
            >
              New group
            </Button>
          ) : undefined
        }
      />
      <Section>
        {groups.isPending && <LoadingState label="Loading group chats" />}
        {groups.isError && <ErrorState message={errorMessage(groups.error)} />}
        {groups.data?.length === 0 && (
          <EmptyState title="No group chats yet" detail="Create one to get started." />
        )}
        {groups.data !== undefined && groups.data.length > 0 && (
          <Table size="small" aria-label="Group chats">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Members</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.data.map((group) => (
                <TableRow key={group.id} hover>
                  <TableCell>
                    <RouterLink to={`/admin/chat-groups/${encodeURIComponent(group.id)}`}>
                      {group.title}
                    </RouterLink>
                  </TableCell>
                  <TableCell>{group.memberCount}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={group.status === 'active' ? 'Active' : 'Archived'}
                      color={group.status === 'active' ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{relativeTime(group.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
      {creating && (
        <NewGroupDialog
          me={me}
          onClose={() => {
            setCreating(false);
          }}
          onCreated={(group) => {
            void navigate(`/admin/chat-groups/${encodeURIComponent(group.id)}`);
          }}
        />
      )}
    </Stack>
  );
}

/**
 * Creating a group chat (FRD-CORE-048): its title and first members. Used from Administration ›
 * Group chats and, for chat administrators, from Chat. `initialMembers` are listed already and can
 * be removed; the creator is a member only if listed (the API adds nobody by itself).
 */
export function NewGroupDialog({
  me,
  initialMembers = [],
  onClose,
  onCreated,
}: {
  me: MeResponse;
  initialMembers?: readonly ChatPersonDto[];
  onClose: () => void;
  onCreated: (group: ChatGroupDetailDto) => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [members, setMembers] = useState<ChatPersonDto[]>([...initialMembers]);
  const [picked, setPicked] = useState<ChatPersonDto | null>(null);
  const create = useMutation({
    mutationFn: (): Promise<ChatGroupDetailDto> =>
      postJson(
        CHAT_GROUPS,
        { title: title.trim(), memberUserIds: members.map((m) => m.userId) },
        ChatGroupDetailSchema,
        me.csrfToken,
      ),
    onSuccess: async (group) => {
      await queryClient.invalidateQueries({ queryKey: CHAT_GROUPS_KEY });
      onCreated(group);
    },
  });
  return (
    <FormDialog
      open
      title="New group chat"
      submitLabel="Create group"
      canSubmit={title.trim().length > 0}
      submitting={create.isPending}
      error={create.error === null ? null : errorMessage(create.error)}
      onClose={onClose}
      onSubmit={() => {
        create.mutate();
      }}
    >
      <Stack spacing={2}>
        <TextField
          label="Title"
          required
          value={title}
          onChange={(event) => {
            setTitle(event.target.value);
          }}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Box sx={{ flexGrow: 1 }}>
            <PersonPicker
              label="Add a member"
              value={picked}
              onChange={setPicked}
              exclude={members.map((m) => m.userId)}
            />
          </Box>
          <Button
            disabled={picked === null}
            onClick={() => {
              if (picked !== null) setMembers((current) => [...current, picked]);
              setPicked(null);
            }}
          >
            Add
          </Button>
        </Stack>
        {members.length > 0 && (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {members.map((member) => (
              <Chip
                key={member.userId}
                label={<PersonName name={member.displayName} userId={member.userId} size={18} />}
                onDelete={() => {
                  setMembers((current) => current.filter((m) => m.userId !== member.userId));
                }}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </FormDialog>
  );
}

/** One group chat: rename, archive, members and moderation of its messages. */
export function ChatGroupDetailPage() {
  const session = useSession();
  const me = session.data;
  const { groupId = '' } = useParams();
  const group = useQuery({
    queryKey: groupKey(groupId),
    queryFn: () => getData(`${CHAT_GROUPS}/${encodeURIComponent(groupId)}`, ChatGroupDetailSchema),
  });
  if (me === null || me === undefined) return null;
  if (group.isPending) return <LoadingState label="Loading group chat" />;
  if (group.isError) return <ErrorState message={errorMessage(group.error)} />;
  return <GroupDetail me={me} group={group.data} />;
}

function GroupDetail({ me, group }: { me: MeResponse; group: ChatGroupDetailDto }) {
  const queryClient = useQueryClient();
  const canChange = hasCapability(me, 'platform.chat_group:change');
  const active = group.status === 'active';
  const base = `${CHAT_GROUPS}/${encodeURIComponent(group.id)}`;
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(group.title);
  const [archiving, setArchiving] = useState(false);
  const [adding, setAdding] = useState<ChatPersonDto | null>(null);
  const [removingMember, setRemovingMember] = useState<ChatPersonDto | null>(null);
  const [removingMessage, setRemovingMessage] = useState<ChatMessageDto | null>(null);
  const [editingMessage, setEditingMessage] = useState<ChatMessageDto | null>(null);
  const [editBody, setEditBody] = useState('');
  const refresh = () => queryClient.invalidateQueries({ queryKey: CHAT_GROUPS_KEY });

  const messages = useQuery({
    queryKey: [...groupKey(group.id), 'messages'],
    enabled: canChange,
    queryFn: () => getData(`${base}/messages?limit=${String(MESSAGE_PAGE)}`, MessagesSchema),
  });
  const update = useMutation({
    mutationFn: (changes: { title?: string; status?: 'archived' }) =>
      patchJson(base, changes, ChatGroupDetailSchema, me.csrfToken, group.version),
    onSuccess: refresh,
  });
  const addMember = useMutation({
    mutationFn: (userId: string) =>
      postJson(`${base}/members`, { userId }, ChatGroupDetailSchema, me.csrfToken),
    onSuccess: refresh,
  });
  const removeMember = useMutation({
    mutationFn: (userId: string) =>
      postJson(
        `${base}/members/${encodeURIComponent(userId)}/remove`,
        {},
        ChatGroupDetailSchema,
        me.csrfToken,
      ),
    onSuccess: refresh,
  });
  const removeMessage = useMutation({
    mutationFn: (message: ChatMessageDto) =>
      postJson(
        `${base}/messages/${encodeURIComponent(message.id)}/remove`,
        {},
        ChatMessageSchema,
        me.csrfToken,
      ),
    onSuccess: refresh,
  });
  const editMessage = useMutation({
    mutationFn: (input: { message: ChatMessageDto; body: string }) =>
      patchJson(
        `${base}/messages/${encodeURIComponent(input.message.id)}`,
        { body: input.body },
        ChatMessageSchema,
        me.csrfToken,
        input.message.version,
      ),
    onSuccess: refresh,
  });

  const newestFirst = [...(messages.data ?? [])];

  return (
    <Stack spacing={3}>
      <PageHeader
        title={group.title}
        description={
          active
            ? 'A group chat. Only its members see and post in it.'
            : 'An archived group chat: read-only for its members.'
        }
        actions={
          canChange && active ? (
            <>
              <Button
                onClick={() => {
                  setTitle(group.title);
                  setRenaming(true);
                }}
              >
                Rename
              </Button>
              <Button
                color="warning"
                onClick={() => {
                  setArchiving(true);
                }}
              >
                Archive
              </Button>
            </>
          ) : undefined
        }
      />
      <Section title={`Members (${String(group.members.length)})`}>
        <Box sx={{ p: 2 }}>
          {canChange && active && (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
              <Box sx={{ flexGrow: 1, maxWidth: 420 }}>
                <PersonPicker
                  label="Add a member"
                  value={adding}
                  onChange={setAdding}
                  exclude={group.members.map((m) => m.userId)}
                />
              </Box>
              <Button
                variant="contained"
                disabled={adding === null || addMember.isPending}
                onClick={() => {
                  if (adding === null) return;
                  addMember.mutate(adding.userId, {
                    onSuccess: () => {
                      setAdding(null);
                    },
                  });
                }}
              >
                Add
              </Button>
            </Stack>
          )}
          {addMember.error !== null && <ErrorState message={errorMessage(addMember.error)} />}
          {group.members.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No members.
            </Typography>
          ) : (
            <List dense aria-label="Members">
              {group.members.map((member) => (
                <ListItem
                  key={member.userId}
                  secondaryAction={
                    canChange && active ? (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          setRemovingMember(member);
                        }}
                      >
                        Remove
                      </Button>
                    ) : undefined
                  }
                >
                  <ListItemText
                    primary={
                      <PersonName name={member.displayName} userId={member.userId} size={24} />
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Section>
      {canChange && (
        <Section title="Messages (moderation)">
          <Box sx={{ p: 2 }}>
            {messages.isPending && <LoadingState label="Loading messages" />}
            {messages.isError && <ErrorState message={errorMessage(messages.error)} />}
            {messages.data?.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No messages yet.
              </Typography>
            )}
            <Stack spacing={1}>
              {newestFirst.map((message) => {
                const placeholder = placeholderFor(message);
                return (
                  <Box
                    key={message.id}
                    component="article"
                    aria-label={`Message from ${message.author.displayName}`}
                    sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}
                  >
                    <Typography variant="subtitle2" component="span">
                      <PersonName
                        name={message.author.displayName}
                        userId={message.author.userId}
                        size={28}
                      />
                    </Typography>{' '}
                    <Typography variant="caption" color="text.secondary">
                      {relativeTime(message.postedAt)}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={placeholder === null ? 'text.primary' : 'text.secondary'}
                      sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                    >
                      {placeholder ?? message.body}
                    </Typography>
                    {placeholder === null && active && (
                      <Stack direction="row" spacing={0.5}>
                        <Button
                          size="small"
                          onClick={() => {
                            setEditBody(message.body ?? '');
                            setEditingMessage(message);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            setRemovingMessage(message);
                          }}
                        >
                          Remove
                        </Button>
                      </Stack>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Section>
      )}
      <FormDialog
        open={renaming}
        title="Rename group chat"
        submitLabel="Save"
        canSubmit={title.trim().length > 0 && title.trim() !== group.title}
        submitting={update.isPending}
        error={update.error === null ? null : errorMessage(update.error)}
        onClose={() => {
          setRenaming(false);
          update.reset();
        }}
        onSubmit={() => {
          update.mutate(
            { title: title.trim() },
            {
              onSuccess: () => {
                setRenaming(false);
              },
            },
          );
        }}
      >
        <TextField
          label="Title"
          required
          fullWidth
          value={title}
          onChange={(event) => {
            setTitle(event.target.value);
          }}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
      </FormDialog>
      <ConfirmDialog
        open={archiving}
        title="Archive group chat"
        message="Members keep reading it, but no one can post, and it cannot be reopened."
        confirmLabel="Archive"
        tone="warning"
        busy={update.isPending}
        error={update.error === null ? null : errorMessage(update.error)}
        onCancel={() => {
          setArchiving(false);
          update.reset();
        }}
        onConfirm={() => {
          update.mutate(
            { status: 'archived' },
            {
              onSuccess: () => {
                setArchiving(false);
              },
            },
          );
        }}
      />
      <ConfirmDialog
        open={removingMember !== null}
        title="Remove member"
        message={`${removingMember?.displayName ?? ''} will no longer see this group or its earlier messages.`}
        confirmLabel="Remove"
        tone="error"
        busy={removeMember.isPending}
        error={removeMember.error === null ? null : errorMessage(removeMember.error)}
        onCancel={() => {
          setRemovingMember(null);
          removeMember.reset();
        }}
        onConfirm={() => {
          if (removingMember === null) return;
          removeMember.mutate(removingMember.userId, {
            onSuccess: () => {
              setRemovingMember(null);
            },
          });
        }}
      />
      <ConfirmDialog
        open={removingMessage !== null}
        title="Remove message"
        message="The message will be hidden from every member. Its text is kept in the audit trail."
        confirmLabel="Remove"
        tone="error"
        busy={removeMessage.isPending}
        error={removeMessage.error === null ? null : errorMessage(removeMessage.error)}
        onCancel={() => {
          setRemovingMessage(null);
          removeMessage.reset();
        }}
        onConfirm={() => {
          if (removingMessage === null) return;
          removeMessage.mutate(removingMessage, {
            onSuccess: () => {
              setRemovingMessage(null);
            },
          });
        }}
      />
      <FormDialog
        open={editingMessage !== null}
        title="Edit message"
        submitLabel="Save"
        canSubmit={editBody.trim().length > 0 && editBody.trim() !== editingMessage?.body}
        submitting={editMessage.isPending}
        error={editMessage.error === null ? null : errorMessage(editMessage.error)}
        onClose={() => {
          setEditingMessage(null);
          editMessage.reset();
        }}
        onSubmit={() => {
          if (editingMessage === null) return;
          editMessage.mutate(
            { message: editingMessage, body: editBody.trim() },
            {
              onSuccess: () => {
                setEditingMessage(null);
              },
            },
          );
        }}
      >
        <TextField
          label="Message"
          multiline
          minRows={2}
          fullWidth
          value={editBody}
          onChange={(event) => {
            setEditBody(event.target.value);
          }}
          slotProps={{ htmlInput: { maxLength: CHAT_MESSAGE_MAX_LENGTH } }}
        />
      </FormDialog>
    </Stack>
  );
}
